import React, { FC, useContext, useMemo } from 'react';
import classNames from 'classnames';
import ReorderResizeTable from 'components/ReorderResizeTable';

import {
  COLUMNS,
  CUSTOM_COL_SIZES,
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  ActivityMetricsTableData,
  BODY_CONTENT_GENERATE,
} from './data';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSellingPortfolio } from 'utils';
import AuthContext from 'providers/Auth/Auth.context';
import {
  ColumnFilterOption,
  ColumnSizing,
} from 'components/ReorderResizeTable/types';

import './ActivityMetricsTable.styles.scss';
import './ActivityMetricsTable.styles.responsive.scss';

type ActivityMetricsTableProps = {
  className?: string;
  isLoading?: boolean;
  totalPages?: number;
  totalItems?: number;
  data: ActivityMetricsTableData[];
  columnFilterOptions?: Partial<
    Record<keyof ActivityMetricsTableData, ColumnFilterOption>
  >;
  columns: typeof COLUMNS;
  columnSizing: ColumnSizing;
  onReorder: (columns: typeof COLUMNS) => void;
  onColumnSizingChange: (columnSizing: ColumnSizing) => void;
};

const ActivityMetricsTable: FC<ActivityMetricsTableProps> = (props) => {
  const {
    className,
    isLoading,
    totalPages = 1,
    totalItems,
    data,
    columnFilterOptions,
    columns,
    columnSizing,
    onReorder,
    onColumnSizingChange,
  } = props;

  const { updateLatestActivityMetricsLocation } = useContext(AuthContext);

  const location = useLocation();

  const classes = classNames('activity-metrics-table', className);

  const tableContentTransformations = useMemo(
    () =>
      COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (data: ActivityMetricsTableData) =>
            BODY_CONTENT_GENERATE[col]({
              data,
            }),
        }),
        {},
      ),
    [],
  );

  const navigate = useNavigate();

  return (
    <ReorderResizeTable
      className={classes}
      totalPages={totalPages}
      totalItems={totalItems}
      data={data}
      columns={columns}
      headingTranslations={HEADING_TRANSLATIONS}
      sortingValues={SORTING_VALUES}
      columnFilterOptions={columnFilterOptions}
      isDataLoading={isLoading}
      tableContentTransformations={tableContentTransformations}
      customSizes={CUSTOM_COL_SIZES}
      onRowClick={({ dealId }) => {
        updateLatestActivityMetricsLocation(location);
        navigate(getSellingPortfolio(dealId, 'deal'));
      }}
      columnSizing={columnSizing}
      onColumnSizingChange={onColumnSizingChange}
      onReorder={onReorder}
    />
  );
};

export default ActivityMetricsTable;
